import React from 'react';
import { FiCheckCircle, FiArrowRight, FiMessageCircle, FiCpu, FiSend, FiUsers, FiBarChart, FiAward } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';

const WhatsAppAPIPage = () => {
  const features = [
    {
      icon: FiMessageCircle,
      title: "Business API",
      description: "Official WhatsApp Business API for verified business accounts and enterprise messaging"
    },
    {
      icon: FiCpu,
      title: "Chatbots",
      description: "AI-powered chatbots for automated customer service and instant responses 24/7"
    },
    {
      icon: FiSend,
      title: "Bulk Messaging",
      description: "Send promotional messages, updates, and notifications to thousands of customers"
    },
    {
      icon: FiUsers,
      title: "Customer Support",
      description: "Provide real-time customer support with multimedia messages and quick replies"
    },
    {
      icon: FiBarChart,
      title: "Analytics & Reports",
      description: "Track message delivery, read rates, response times, and customer engagement"
    },
    {
      icon: FiAward,
      title: "CRM Integration",
      description: "Seamless integration with your existing CRM and business management systems"
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "₹20,000",
      period: "/month",
      description: "Perfect for small businesses starting with WhatsApp",
      features: [
        "Up to 5,000 Messages/month",
        "Business API Setup",
        "Basic Chatbot (5 flows)",
        "Green Tick Verification Support",
        "Message Templates Creation",
        "Monthly Analytics Report",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Growth",
      price: "₹40,000",
      period: "/month",
      description: "Ideal for growing businesses scaling WhatsApp presence",
      features: [
        "Up to 20,000 Messages/month",
        "Advanced Chatbot (15 flows)",
        "CRM Integration",
        "Bulk Messaging Campaigns",
        "Multi-Agent Support",
        "Custom Templates",
        "Bi-weekly Reports",
        "Phone + Email Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large businesses needing full WhatsApp solutions",
      features: [
        "Unlimited Messages",
        "AI-Powered Chatbots",
        "Custom API Development",
        "Advanced Automation",
        "Dedicated WhatsApp Number",
        "24/7 Technical Support",
        "Weekly Performance Calls",
        "Dedicated Account Manager"
      ],
      highlighted: false
    }
  ];

  const whyChooseUs = [
    {
      title: "Official WhatsApp Partner",
      description: "We are an authorized WhatsApp Business Solution Provider with direct Meta partnership"
    },
    {
      title: "Quick Verification Process",
      description: "We handle the entire business verification process to get your green tick approved fast"
    },
    {
      title: "Custom Chatbot Development",
      description: "AI-powered chatbots tailored to your business needs with natural language processing"
    },
    {
      title: "Compliance & Security",
      description: "Ensure full compliance with WhatsApp policies and end-to-end encryption for messages"
    }
  ];

  const faqs = [
    {
      question: "What is WhatsApp Business API and how is it different from WhatsApp Business app?",
      answer: "WhatsApp Business API is designed for medium to large businesses, offering unlimited messaging, multiple users, chatbots, and API integration with your systems. The regular WhatsApp Business app is limited to single device use and manual messaging. The API enables automation, bulk messaging, and enterprise-level features."
    },
    {
      question: "How much does WhatsApp Business API cost?",
      answer: "Pricing includes our management fee plus WhatsApp's conversation-based pricing (charged by Meta). In India, business-initiated conversations cost approximately ₹0.40-₹1.00 per conversation depending on category. Our plans include message allowances, and we optimize to minimize Meta charges."
    },
    {
      question: "How do I get verified with a green tick on WhatsApp?",
      answer: "We handle the entire verification process! Requirements include a registered business, verified Facebook Business Manager, phone number, and business documentation. The process takes 7-14 days once submitted. Our team ensures your application meets all criteria for approval."
    },
    {
      question: "Can I send promotional messages to all my customers?",
      answer: "You can only send messages to customers who have opted in to receive communications from your business. We help create opt-in campaigns and manage subscriber lists. Message templates must be pre-approved by WhatsApp, and we handle template creation and approval."
    },
    {
      question: "What types of messages can I send via WhatsApp Business API?",
      answer: "You can send notifications, order updates, appointment reminders, customer support messages, promotional offers (with opt-in), OTPs, delivery updates, and more. Messages can include text, images, videos, documents, location, and interactive buttons."
    },
    {
      question: "How do chatbots work with WhatsApp Business API?",
      answer: "Our chatbots use AI and predefined flows to handle customer queries automatically. They can answer FAQs, collect information, schedule appointments, track orders, and escalate complex issues to human agents. We customize chatbot personalities and responses for your brand."
    },
    {
      question: "Can WhatsApp API integrate with my existing CRM or systems?",
      answer: "Yes! We integrate WhatsApp API with popular CRMs like Salesforce, HubSpot, Zoho, and custom systems via webhooks and APIs. This enables automatic sync of customer data, conversation history, and triggers based on customer actions in your system."
    },
    {
      question: "What happens if I exceed my monthly message limit?",
      answer: "You can easily upgrade your plan or purchase additional message credits. We monitor your usage and notify you before you reach limits. Unlike some providers, we never stop your service—you simply pay for additional conversations as needed."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              WhatsApp Business API Services
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              Connect with 2+ billion users through automated messaging, chatbots, and customer support
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                className="bg-white text-[#25D366] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                View Pricing
              </a>
              <a
                href="#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#25D366] transition-colors inline-block"
              >
                Get Free Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">
              What is WhatsApp Business API?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                WhatsApp Business API is an enterprise solution that enables medium to large businesses to communicate with customers at scale on WhatsApp. With over 2 billion active users, WhatsApp is the most popular messaging app globally, making it essential for customer engagement. The API allows businesses to send automated messages, provide customer support, integrate with CRM systems, and deploy AI-powered chatbots.
              </p>
              <p className="text-lg leading-relaxed">
                Unlike the regular WhatsApp Business app limited to small businesses, the API supports multiple users, unlimited messaging, advanced automation, and enterprise integrations. Businesses achieve 98% open rates and 45-60% click-through rates on WhatsApp—far exceeding email marketing. Our WhatsApp Business API service handles setup, verification, chatbot development, campaign management, and ongoing optimization to help you leverage this powerful platform effectively.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our WhatsApp Business API Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-[#25D366] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 text-[#25D366]">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-[#25D366] text-white shadow-2xl scale-105 border-4 border-[#25D366]'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#25D366] px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#25D366]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-lg ${
                      plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#25D366]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-gray-100' : 'text-gray-700'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#25D366] hover:bg-gray-100'
                      : 'bg-[#25D366] text-white hover:bg-[#128C7E]'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Choose Mirani Marketing for WhatsApp API?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#25D366] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-[#25D366] text-2xl">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Transform Customer Communication?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-100">
            Get a free demo of WhatsApp Business API and see how it can boost customer engagement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-[#25D366] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Free Demo
            </a>
            <a
              href="tel:+919876543210"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#25D366] transition-colors inline-block"
            >
              Call: +91 98765 43210
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatsAppAPIPage;

